import {useState} from "react";
import {useBookingData} from "../../../container/booking/booking-context";
import {useCalendar} from "../../../hooks/use-calendar";
import {
  UnavailableDateRange,
  UseBookingTimeSlotsResult,
  Weekday,
  weekdayFromDate,
  weekdayIndex,
} from "../../../features/booking-availability";
import {CalendarBody} from "../calendar-body";
import {CalendarHeader} from "../calendar-header";
import {StyledCalendarWrapper} from "../calendar.style";

/**
 * Calendar grid for booking the next session of a purchased bundle.
 *
 * The schedule is passed in rather than fetched here: the grid is built once on
 * mount, so the owner has to hold this component back until the weekdays have
 * resolved (see `useBookingSchedule`).
 */
const BundleCalendar = ({
  timeSlots,
  mentorId,
  serviceId,
  availableWeekdays,
  unavailableDates,
}: {
  timeSlots: UseBookingTimeSlotsResult;
  mentorId: number;
  serviceId: number;
  availableWeekdays: Weekday[];
  unavailableDates: UnavailableDateRange[];
}) => {
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());

  const {setValues, data} = useBookingData();

  function getDatesInRange(startDate: Date, endDate: Date) {
    const date = new Date(startDate.getTime());

    const dates = [];

    while (date <= endDate) {
      const dateToAdd = new Date(date);
      dates.push(
        `${dateToAdd.getFullYear()}-${dateToAdd.getMonth() + 1}-${dateToAdd.getDate()}`
      );
      date.setDate(date.getDate() + 1);
    }

    return dates;
  }

  const unavailableDateRangesState = Array.from(
    new Set(
      unavailableDates.flatMap((date) =>
        getDatesInRange(new Date(date.from), new Date(date.to))
      )
    )
  );

  // v2 hands back the bookable weekdays already inverted; the calendar grid
  // works in Date.getDay() indices.
  const arrDaysNum = availableWeekdays.map(weekdayIndex);

  const {
    days,
    currentMonth: newCurrentMonth,
    currentYear: year,
    currentSelectedDate,
    setCurrentSelectedDate,
  } = useCalendar(currentMonth, currentYear, arrDaysNum, unavailableDateRangesState);

  const handlePrev = () => {
    setCurrentMonth((prevState) => prevState - 1);
    if (currentMonth < 1) {
      setCurrentMonth(11);
      setCurrentYear((prevState) => prevState - 1);
    }
  };

  const handleNext = () => {
    setCurrentMonth((prevState) => prevState + 1);
    if (currentMonth > 10) {
      setCurrentMonth(0);
      setCurrentYear((prevState) => prevState + 1);
    }
  };
  const handleSetCurrentDate = (day: number | null) => {
    if (!day) {
      return;
    }
    const selectedDateString = new Date(year, currentMonth, day);
    setValues({
      ...data,
      selectedDateString,
      booking_date: day,
      booking_year: year,
      booking_month: currentMonth + 1,
    });
    setCurrentSelectedDate(selectedDateString);

    // No mentorTimeZone: v2 resolves the coach's timezone server-side.
    timeSlots.fetchTimeSlots({
      mentorId,
      serviceId,
      date: day,
      year,
      month: currentMonth + 1,
      day: weekdayFromDate(selectedDateString),
      userTimeZone: data.selectedTimeZone as string,
    });
  };
  return (
    <StyledCalendarWrapper>
      <CalendarHeader
        currentMonth={newCurrentMonth}
        year={year}
        month={currentMonth}
        currentSelectedDate={currentSelectedDate}
        handleNext={handleNext}
        handlePrev={handlePrev}
      />
      <CalendarBody
        days={days}
        setCurrentDate={handleSetCurrentDate}
        currentSelectedDate={currentSelectedDate}
      />
    </StyledCalendarWrapper>
  );
};

export {BundleCalendar};
